import {Badge, Layout, Menu} from 'antd';
import React, {useMemo} from 'react';
import {useLocation} from 'react-router';
import {Link} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import integrationsIcon from '../../libs/ui/src/assets/svg/integrations.svg';

import connector from './connector';
import {brandConfig, CONSTANTS, IconWrapper, isClientTrial} from '../../libs/ui/src';
import {getUsersWithErrors, isPaidClient} from '../../utils';
import {InboxNavigation as getInboxNavigation} from './inboxNavigation';

import './navigation.scss';

const {Sider} = Layout;

const Navigation = ({
  isNavCollapsed,
  setSettings,
  client,
  users,
  unreadCountByUser,
  inboxLoading,
  isAdmin,
  openMenuItems,
}: any) => {
  const location = useLocation();
  const pages = getPages(client, isAdmin);
  const filteredPathname = `/${location.pathname.split('/')[1]}`;
  const selectedInboxUser = location.pathname.split('/').pop();

  const selectedKeys = useMemo(() => {
    const array = pages.reduce(
      (acc, item) => (item.path === filteredPathname ? acc.concat(item.key) : acc),
      [] as string[]
    );
    if (array[0] === CONSTANTS.inbox_page_key) {
      array.push(selectedInboxUser);
    }
    return array;
  }, [pages, filteredPathname, selectedInboxUser]);

  const unreadCountForAllUser = useMemo(() => {
    return (users || []).filter(x => getUsersWithErrors(x, isAdmin)).length;
  }, [users, isAdmin]);

  const onOpenChange = (keys: any) => {
    if (isNavCollapsed) {
      if (keys.length > 0) {
        setSettings({openMenuItems: keys});
      } else {
        setSettings({openMenuItems: []});
      }
    }
  };

  const items = pages.map(page => {
    if (page.key === 'inbox') {
      return getInboxNavigation({
        page,
        client,
        users,
        unreadCountByUser,
        inboxLoading,
        selectedInboxUser,
        openMenuItems,
        setSettings,
        isNavCollapsed,
      });
    }
    return ({
      label: (
        <Link to={page.path}>
          <span>
            {page.label}{' '}
            {page.label === 'Users' && <Badge className="users-error-count" count={unreadCountForAllUser} />}
          </span>
        </Link>
      ),
      key: page.key,
      icon: page.icon,
    })
  });
  if (brandConfig.isGx) {
    items.push(
      {
        label: (
          <a href="https://help.cuegrowth.ai/" target="_blank" rel="noreferrer">
            <span>Resources</span>
          </a>
        ),
        key: 'resources',
        icon: (
          <IconWrapper>
            <FontAwesomeIcon icon={{prefix: 'fas', iconName: 'book-open'}} />
          </IconWrapper>
        )
      }
    )
  }

  return (
    <Sider
      theme="light"
      className="navigation"
      collapsible
      collapsed={isNavCollapsed}
      onCollapse={() => {
        setSettings({
          isNavCollapsed: !isNavCollapsed,
          openMenuItems: !isNavCollapsed ? [] : [CONSTANTS.inbox_page_key],
        });
      }}
    >
      <Menu
        className="menu-tabs"
        mode="inline"
        theme="light"
        onOpenChange={onOpenChange}
        openKeys={openMenuItems}
        selectedKeys={selectedKeys}
        items={items}
      />
    </Sider>
  );
};

function getPages(client: any, isAdmin: boolean) {
  const excludeList: any = [];
  const isTrialSubscription = isClientTrial(client.type);
  if (isTrialSubscription) {
    excludeList.push('prospects');
  }
  if (!client.inbox_feature) {
    excludeList.push('inbox');
  }
  if (!isAdmin) {
    excludeList.push('audience');
    excludeList.push('tasks');
  }

  if(!isPaidClient(client)) {
    excludeList.push('audience-lists');
    excludeList.push('demos');
    excludeList.push('crm');
  } 

  const filteredPages = pageList.filter((page: any) => !excludeList.find((excludeLabel: any) => excludeLabel === page.key));

  return filteredPages;
}

const pageList = [
  {
    label: 'Campaigns',
    path: '/',
    icon: (
      <IconWrapper>
        <FontAwesomeIcon icon={{prefix: 'fas', iconName: 'list-ul'}} />
      </IconWrapper>
    ),
    key: 'campaigns',
  },
  {
    label: 'Audience Lists',
    path: '/audience-lists',
    icon: (
      <IconWrapper>
        <FontAwesomeIcon icon={{prefix: 'fas', iconName: 'users'}} />
      </IconWrapper>
    ),
    key: 'audience-lists',
  },
  {
    label: 'Lead Enrichment',
    path: '/lead-enrichment',
    icon: (
      <IconWrapper>
        <FontAwesomeIcon icon={{prefix: 'fas', iconName: 'user-plus'}} />
      </IconWrapper>
    ),
    key: 'lead-enrichment',
  },
  {
    label: 'Users',
    path: '/users',
    icon: (
      <IconWrapper>
        <FontAwesomeIcon icon={{prefix: 'fas', iconName: 'users'}} />
      </IconWrapper>
    ),
    key: 'users',
  },
  {
    label: 'Prospects',
    path: '/prospects',
    icon: (
      <IconWrapper>
        <FontAwesomeIcon icon={{prefix: 'fas', iconName: 'user-clock'}} />
      </IconWrapper>
    ),
    key: 'prospects',
  },
  {
    label: 'Connections',
    path: '/connections',
    icon: (
      <IconWrapper>
        <FontAwesomeIcon icon={{prefix: 'fas', iconName: 'project-diagram'}} />
      </IconWrapper>
    ),
    key: 'connections',
  },
  {
    label: 'CRM',
    path: '/crm',
    icon: (
      <IconWrapper>
        <FontAwesomeIcon icon={{prefix: 'fas', iconName: 'address-book'}} />
      </IconWrapper>
    ),
    key: 'crm',
  },
  {
    label: 'Inbox',
    path: '/inbox',
    key: 'inbox',
  },
  {
    label: 'Templates',
    path: '/templates',
    icon: (
      <IconWrapper>
        <FontAwesomeIcon icon={{prefix: 'fas', iconName: 'file-alt'}} />
      </IconWrapper>
    ),
    key: 'templates',
  },
  {
    label: 'Tasks',
    path: '/tasks',
    icon: (
      <IconWrapper>
        <FontAwesomeIcon icon={{prefix: 'fas', iconName: 'tasks'}} />
      </IconWrapper>
    ),
    key: 'tasks',
  },
  {
    label: 'Integrations',
    path: '/integrations',
    icon: (
      <IconWrapper>
        <img src={integrationsIcon} alt="integrations" />
      </IconWrapper>
    ),
    key: 'integrations',
  },
  {
    label: 'Product Demos',
    path: '/demos',
    icon: (
      <IconWrapper>
        <FontAwesomeIcon icon={{prefix: 'fas', iconName: 'video'}} />
      </IconWrapper>
    ),
    key: 'demos',
  },
];

export default connector(Navigation);
